import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { EditLock } from "@/utils/editLock";

export interface ReservationState {
  reservationId?: string;
  restaurantId?: string;
  restaurantName: string;
  reservationTime: string;
  reservationDate: string;
  // The reservation's absolute instant, exactly as the backend sent it (UTC
  // ISO-8601). `reservationTime`/`reservationDate` above are locale-formatted
  // display strings derived from this and are NOT safe to re-parse; anything
  // that needs the actual moment reads this. Optional because state persisted
  // before this field existed rehydrates without it.
  reservationAtIso?: string;
  // The restaurant's IANA zone (e.g. "America/Los_Angeles") the display
  // strings above were rendered in. Lets a screen format another label from
  // `reservationAtIso` in that same zone. Undefined when the restaurant doc
  // has none (labels then use the device zone, as the bootstrap does).
  restaurantTimeZone?: string;
  partySize: number;
  timeRemainingLabel: string;
  expiresAtMs?: number;
  // Countdown cutoff time; should be reservation.lockAt in UTC ms
  lockAtMs?: number;
  // Localized human label for lockAt (e.g., "Oct 3, 7:30 PM")
  lockAtLabel?: string;
  // Whether countdown should actively tick; false when > 90 minutes away
  countdownActive?: boolean;
  // Server-reported edit lock (FOYR-432): locked, and whether the clock or a
  // POS send closed the order. Undefined from a backend that predates it, and
  // then only the local clock (expiresAtMs) decides.
  editLock?: EditLock;
  phoneNumber?: string;
  // Last four digits of the guest's phone, for "Code sent to ···1234"
  // (FOYR-566). Undefined until the backend sends it.
  phoneLast4?: string;
  guestName?: string;
  order?: any;
  orderItems: Array<{
    id?: string;
    name?: string;
    quantity?: number;
    priceCentsPerUnit?: number;
    modifiers?: any;
  }>; // normalized list for UI
  logo?: string;
  banner?: string;
  // Restaurant street address, e.g. "742 Evergreen Terrace, Springfield".
  // Landed by a parallel backend PR (Restaurant.address) - stays undefined until then.
  address?: string;
  loaded?: boolean;
  // True when an order has just been confirmed via OTP (used to gate success page)
  orderConfirmed?: boolean;
}

const initialState: ReservationState = {
  reservationId: undefined,
  restaurantId: undefined,
  restaurantName: "Aerlume",
  reservationTime: "2:00PM",
  reservationDate: "Monday, October 3, 2025",
  reservationAtIso: undefined,
  restaurantTimeZone: undefined,
  partySize: 4,
  timeRemainingLabel: "32:27",
  expiresAtMs: undefined,
  lockAtMs: undefined,
  lockAtLabel: undefined,
  countdownActive: false,
  editLock: undefined,
  phoneNumber: "",
  guestName: "",
  orderItems: [],
  logo: "",
  banner: "",
  loaded: false,
  orderConfirmed: false,
};

const reservationSlice = createSlice({
  name: "reservation",
  initialState,
  reducers: {
    updateReservation(state, action: PayloadAction<Partial<ReservationState>>) {
      Object.assign(state, action.payload);
    },
    setReservationId(state, action: PayloadAction<string | undefined>) {
      state.reservationId = action.payload;
    },
    clearReservation(state) {
      Object.assign(state, initialState);
    },
    setPartySize(state, action: PayloadAction<number>) {
      state.partySize = action.payload;
    },
  },
});

export const {
  updateReservation,
  clearReservation,
  setPartySize,
  setReservationId,
} = reservationSlice.actions;
export default reservationSlice.reducer;
